import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormHelperText,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { getPromotionStockByType } from './promotionStockHelpers';

const selectMenuProps = {
  disableScrollLock: true,
  PaperProps: {
    sx: {
      maxHeight: 300,
      overflow: 'auto',
      zIndex: 1400,
      '& .MuiList-root': {
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
  },
};

const buildMenuProps = (custom = {}) => ({
  ...selectMenuProps,
  ...custom,
  PaperProps: {
    ...(selectMenuProps.PaperProps || {}),
    ...(custom.PaperProps || {}),
    sx: {
      ...(selectMenuProps.PaperProps?.sx || {}),
      ...(custom.PaperProps?.sx || {}),
    },
  },
});

const PROMOTION_GROUP_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const v = String(i + 1);
  return { value: v, label: `Group ${v}` };
});

const limitDecimals = (val, max = 2) => {
  if (val === '' || val === null || val === undefined) return '';
  const s = String(val).replace(/[^\d.]/g, '');
  const parts = s.split('.');
  const intPart = parts[0] || '0';
  if (parts.length === 1) return intPart;
  const frac = (parts[1] || '').slice(0, max);
  return frac.length ? `${intPart}.${frac}` : intPart;
};

export default function GroupOffer({
  formData,
  handleInputChange,
  handleOpenProductModal,
  handleRemoveProduct,
  handleProductCompulsoryChange,
  handleProductQuantityChange,
  handleProductTypeChange,
  handleProductPricingChange,
  handleProductItemDiscountTypeChange,
  handleProductItemDiscountValueChange,
  buyProductsSearch,
  setBuyProductsSearch,
  getProductsSearch,
  setGetProductsSearch,
  errors = {},
  readOnly = false,
  isViewMode = false,
}) {
  const getProductTypeOptions = (split) => {
    if (split === 0) return [{ value: 'Box', label: 'Box' }];
    if (split === 1) return [{ value: 'Piece', label: 'Piece' }];
    if (split === 2)
      return [
        { value: 'Box', label: 'Box' },
        { value: 'Piece', label: 'Piece' },
      ];
    return [{ value: 'Box', label: 'Box' }];
  };

  const getDefaultProductType = (split) => {
    if (split === 0) return 'Box';
    if (split === 1) return 'Piece';
    if (split === 2) return 'Box';
    return 'Box';
  };

  const normalizeTypeValue = (type, split) => {
    if (!type || type === '' || type === 'undefined' || type === 'null' || type === 'Both') {
      return getDefaultProductType(split);
    }
    return type;
  };

  const getSelectedProductType = (product) => {
    return normalizeTypeValue(product.type, product.split);
  };

  const parsePrice = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  };

  const updateProductGroupNo = (originalIndex, value) => {
    const items = [...formData.buyProducts];
    if (!items[originalIndex]) return;
    items[originalIndex] = { ...items[originalIndex], groupNo: value };
    handleInputChange('buyProducts', items);
  };

  const updateGetProductGroupNo = (originalIndex, value) => {
    const items = [...formData.getProducts];
    if (!items[originalIndex]) return;
    items[originalIndex] = { ...items[originalIndex], groupNo: value };
    handleInputChange('getProducts', items);
  };

  const usedBuyGroups = Array.from(
    new Set(
      (formData.buyProducts || [])
        .map((p) => String(p.groupNo || '').trim())
        .filter((g) => PROMOTION_GROUP_OPTIONS.some((o) => o.value === g))
    )
  ).sort((a, b) => Number(a) - Number(b));

  const usedGetGroups = Array.from(
    new Set(
      (formData.getProducts || [])
        .map((p) => String(p.groupNo || '').trim())
        .filter((g) => PROMOTION_GROUP_OPTIONS.some((o) => o.value === g))
    )
  ).sort((a, b) => Number(a) - Number(b));

  const updateBuyGroupQty = (groupKey, value) => {
    if (!(value === '' || /^\d+$/.test(value))) return;
    handleInputChange('buyGroupQuantities', {
      ...(formData.buyGroupQuantities || {}),
      [groupKey]: value,
    });
  };

  const updateGetGroupQty = (groupKey, value) => {
    if (!(value === '' || /^\d+$/.test(value))) return;
    handleInputChange('getGroupQuantities', {
      ...(formData.getGroupQuantities || {}),
      [groupKey]: value,
    });
  };

  const renderGroupQtyPanel = ({
    usedGroups,
    quantities,
    onChange,
    totalQty,
    errorKey,
    sideLabel,
  }) => {
    if (usedGroups.length === 0) return null;
    const sum = usedGroups.reduce((acc, g) => acc + (parseInt(quantities?.[g], 10) || 0), 0);
    const total = parseInt(totalQty, 10);
    const exceedsTotal = Number.isFinite(total) && total > 0 && sum > total;

    return (
      <Box
        sx={{
          mt: 1,
          mb: 1,
          p: 1.5,
          border: 1,
          borderColor: errors[errorKey] || exceedsTotal ? 'error.main' : 'divider',
          borderRadius: 1,
          bgcolor: '#f8fafc',
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.85rem' }}>
          Qty per {sideLabel} group (Optional)
          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            (total cannot be greater than Total {sideLabel} Quantity{Number.isFinite(total) && total > 0 ? `: ${total}` : ''})
          </Typography>
        </Typography>
        <Grid container spacing={1.5}>
          {usedGroups.map((groupKey) => (
            <Grid item xs={6} sm={4} key={`${sideLabel}-group-qty-${groupKey}`}>
              <TextField
                fullWidth
                className="input-border"
                size="small"
                label={`Group ${groupKey} Qty (Optional)`}
                placeholder="Optional"
                value={quantities?.[groupKey] ?? ''}
                onChange={(e) => onChange(groupKey, e.target.value)}
                type="number"
                inputProps={{ min: 1, max: 9999 }}
                InputProps={{ readOnly: readOnly, sx: { height: 40 } }}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault();
                }}
                error={!!errors[`${errorKey}_${groupKey}`]}
                helperText={errors[`${errorKey}_${groupKey}`] || ''}
                sx={{
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: errors[errorKey] || exceedsTotal ? 'error.main' : 'text.secondary' }}
        >
          Group qty total: {sum}
          {Number.isFinite(total) && total > 0 ? ` / ${total}` : ''}
          {errors[errorKey] ? ` — ${errors[errorKey]}` : ''}
        </Typography>
      </Box>
    );
  };

  const getPriceByType = (product, mode = 'sales') => {
    const selectedType = getSelectedProductType(product);
    if (selectedType === 'Piece') {
      if (mode === 'offer') {
        return parsePrice(
          product.pieceOfferPrice ??
          product.offerPiecePrice ??
          product.offerPrice ??
          0
        );
      }
      return parsePrice(
        product.pieceSalesPrice ??
        product.piece_sales_price ??
        product.PiecePrice ??
        product.salesPrice ??
        product.price ??
        0
      );
    }
    if (mode === 'offer') {
      return parsePrice(
        product.boxOfferPrice ??
        product.offerBoxPrice ??
        product.offerPrice ??
        0
      );
    }
    return parsePrice(
      product.boxSalesPrice ??
      product.salesPrice ??
      product.price ??
      0
    );
  };

  const formatPrice = (value) => parsePrice(value).toFixed(2);

  const getStockByType = (product) => {
    if (!product) return 0;
    const currentType = product.type || getSelectedProductType(product);
    const selectedType = currentType === 'Piece' || currentType === 'piece' ? 'Piece' :
      currentType === 'Box' || currentType === 'box' ? 'Box' :
        getSelectedProductType(product);
    return getPromotionStockByType(product, selectedType);
  };

  useEffect(() => {
    const buyProductsNeedUpdate = formData.buyProducts.some(product => {
      const normalizedType = normalizeTypeValue(product.type, product.split);
      const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';
      return product.type !== normalizedType || !hasValidPricing;
    });

    if (buyProductsNeedUpdate) {
      const updatedProducts = formData.buyProducts.map(product => {
        const normalizedType = normalizeTypeValue(product.type, product.split);
        const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';

        if (product.type !== normalizedType || !hasValidPricing) {
          return {
            ...product,
            type: normalizedType,
            pricingType: hasValidPricing ? product.pricingType : 'sales',
          };
        }
        return product;
      });
      handleInputChange('buyProducts', updatedProducts);
    }
  }, [formData.buyProducts]);

  useEffect(() => {
    const getProductsNeedUpdate = formData.getProducts.some(product => {
      const normalizedType = normalizeTypeValue(product.type, product.split);
      const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';
      return product.type !== normalizedType || !hasValidPricing;
    });

    if (getProductsNeedUpdate) {
      const updatedProducts = formData.getProducts.map(product => {
        const normalizedType = normalizeTypeValue(product.type, product.split);
        const hasValidPricing = product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null';

        if (product.type !== normalizedType || !hasValidPricing) {
          return {
            ...product,
            type: normalizedType,
            pricingType: hasValidPricing ? product.pricingType : 'sales',
          };
        }
        return product;
      });
      handleInputChange('getProducts', updatedProducts);
    }
  }, [formData.getProducts]);

  const filteredBuyProducts = formData.buyProducts
    .map((product, index) => ({
      ...product,
      type: getSelectedProductType(product),
      pricingType: (product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null') ? product.pricingType : 'sales',
      originalIndex: index
    }))
    .filter((product) => {
      const searchLower = String(buyProductsSearch ?? '').toLowerCase();
      if (!searchLower) return true;
      const name = String(product.name ?? product.product_name ?? '');
      const code = String(product.code ?? product.product_code ?? '');
      return (
        name.toLowerCase().includes(searchLower) ||
        code.toLowerCase().includes(searchLower) ||
        String(product.id ?? '').toLowerCase().includes(searchLower)
      );
    });

  const filteredGetProducts = formData.getProducts
    .map((product, index) => ({
      ...product,
      type: getSelectedProductType(product),
      pricingType: (product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null') ? product.pricingType : 'sales',
      originalIndex: index
    }))
    .filter((product) => {
      const searchLower = String(getProductsSearch ?? '').toLowerCase();
      if (!searchLower) return true;
      const name = String(product.name ?? product.product_name ?? '');
      const code = String(product.code ?? product.product_code ?? '');
      return (
        name.toLowerCase().includes(searchLower) ||
        code.toLowerCase().includes(searchLower) ||
        String(product.id ?? '').toLowerCase().includes(searchLower)
      );
    });

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#003DA5', fontSize: '2rem' }}>
          Group Offer Configuration
        </Typography>

        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Buy specified items and get another product at discounted price or set value
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Configure bulk or per-item discounts on buy products
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: '0.6rem' }}>
                • Set discount value or percentage off for get products
              </Typography>
            </Box>
          }
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#1e3a5f',
                color: 'white',
                fontSize: '0.465rem',
                maxWidth: 500,
                boxShadow: 2,
                '& .MuiTooltip-arrow': {
                  color: '#1e3a5f',
                },
              },
            },
          }}
        >
          <IconButton size="small" sx={{ color: '#003DA5' }}>
            <Iconify icon="eva:info-fill" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider color="black" />

      {errors.buyGroupQty || errors.getGroupQty ? (
        <Box sx={{ mt: 1.5, mb: 0.5 }}>
          {errors.buyGroupQty ? (
            <Typography variant="body2" color="error" sx={{ display: 'block', mb: errors.getGroupQty ? 1 : 0 }}>
              {errors.buyGroupQty}
            </Typography>
          ) : null}
          {errors.getGroupQty ? (
            <Typography variant="body2" color="error" sx={{ display: 'block' }}>
              {errors.getGroupQty}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
            Buy Products
          </Typography>

          <TextField
            fullWidth
            className="input-border text-field-promo"
            label={
              <span>
                Total Buy Quantity<span style={{ color: 'red' }}>*</span>
              </span>
            }
            value={formData.buyQuantity}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleInputChange('buyQuantity', value);
              }
            }}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onWheel={(e) => e.target.blur()}
            placeholder="Enter total buy quantity"
            type="number"
            inputProps={{ min: 1, max: 9999 }}
            InputProps={{
              readOnly: readOnly,
              sx: { height: 40, }
            }}
            error={!!errors.buyQuantity || !!errors.compulsoryQuantity}
            helperText={errors.buyQuantity || errors.compulsoryQuantity}
            sx={{
              mb: 1,
              '& input[type=number]': { MozAppearance: 'textfield' },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            }}
          />

          {renderGroupQtyPanel({
            usedGroups: usedBuyGroups,
            quantities: formData.buyGroupQuantities || {},
            onChange: updateBuyGroupQty,
            totalQty: formData.buyQuantity,
            errorKey: 'buyGroupQty',
            sideLabel: 'Buy',
          })}

          <Box
            sx={{
              bgcolor: '#fffbea',
              border: 1,
              borderColor: '#ffd700',
              p: 2,
              mb: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Item Discount on Buy Products (Optional)
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Apply Discount To *</InputLabel>
              <Select
                className='input-border'
                value={formData.buyDiscountApplyTo || 'none'}
                label="Apply Discount To *"
                onChange={(e) => handleInputChange('buyDiscountApplyTo', e.target.value)}
                MenuProps={buildMenuProps()}
                disabled={readOnly}
              >
                <MenuItem value="none">No Discount</MenuItem>
                <MenuItem value="bulk">Bulk (All Buy Products Total)</MenuItem>
                <MenuItem value="per-item">Per Item (Each buy products)</MenuItem>
              </Select>
            </FormControl>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 2,
                color: '#d32f2f',
                fontSize: '12px',
                lineHeight: 1.6
              }}
            >
              Per Item: Configure discount for each buy product individually
              <br />
              Bulk: Apply a single discount to the total of all buy products.
            </Typography>

            {formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none' && (
              <>
                {formData.buyDiscountApplyTo === 'bulk' && (
                  <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                      className='input-border'    
                        value={formData.buyDiscountType || 'percentage'}
                        label="Discount Type"
                        onChange={(e) => handleInputChange('buyDiscountType', e.target.value)}
                        MenuProps={buildMenuProps({
                          disablePortal: true,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        })}
                        disabled={readOnly}
                      >
                        <MenuItem value="percentage">Percentage %</MenuItem>
                        <MenuItem value="fixed">Fixed Amount</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <TextField
                      className='input-border text-field'
                        fullWidth
                        size="small"
                        label="Discount Value"
                        placeholder="Enter discount value"
                        value={formData.buyDiscountValue || ''}
                        onChange={(e) => {
                          let raw = e.target.value;
                          if (formData.buyDiscountType === 'percentage') {
                            const num = parseFloat(raw);
                            if (!Number.isNaN(num) && num > 100) {
                              handleInputChange('buyDiscountValue', '100');
                              return;
                            }
                            if (raw === '' || raw === '.') {
                              handleInputChange('buyDiscountValue', raw);
                              return;
                            }
                          }
                          const sanitized = limitDecimals(raw, 2);
                          if (formData.buyDiscountType === 'percentage') {
                            const num = parseFloat(sanitized);
                            if (!Number.isNaN(num) && num > 100) {
                              handleInputChange('buyDiscountValue', '100');
                              return;
                            }
                          }
                          handleInputChange('buyDiscountValue', sanitized);
                        }}
                        onBlur={(e) => {
                          if (formData.buyDiscountType === 'percentage') {
                            const num = parseFloat(e.target.value);
                            if (!Number.isNaN(num) && num > 100) {
                              handleInputChange('buyDiscountValue', '100');
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()}
                        type="number"
                        inputProps={{
                          min: 0,
                          max: formData.buyDiscountType === 'percentage' ? 100 : 999999,
                          step: formData.buyDiscountType === 'percentage' ? 1 : 0.01,
                        }}
                        InputProps={{ readOnly: readOnly }}
                        error={!!errors.buyDiscountValue && !errors.buyDiscountValue?.startsWith('Warning:')}
                      />
                      {errors.buyDiscountValue?.startsWith('Warning:') ? (
                        <FormHelperText sx={{ color: '#ed6c02', fontSize: '0.75rem', mt: 0.5, mx: 1.75 }}>
                          {errors.buyDiscountValue}
                        </FormHelperText>
                      ) : errors.buyDiscountValue ? (
                        <FormHelperText error sx={{ mt: 0.5, mx: 1.75 }}>
                          {errors.buyDiscountValue}
                        </FormHelperText>
                      ) : formData.buyDiscountType === 'percentage' ? (
                        <FormHelperText sx={{ mt: 0.5, mx: 1.75, color: '#000' }}>
                          Maximum 100%
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>

          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Selected Products ({formData.buyProducts.length})
              </Typography>
              {(errors.buyProducts || errors.compulsoryProducts) && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.buyProducts || errors.compulsoryProducts}
                </Typography>
              )}
            </Box>
            {!readOnly && (
              <Button
                variant="outlined"
                className="button-variant-outlined"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => handleOpenProductModal('buy')}
                disabled={formData.buyProducts.length >= 200}
                disableRipple
              >
                Add More Products
              </Button>
            )}
          </Box>
          {formData.buyProducts.length > 0 && (
            <Box sx={{ mt: 1, mb: 2, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  pb: 1,
                  mb: 1,

                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <TextField
                  className="allow-interaction input-border"
                  fullWidth
                  placeholder="Search by product name or product code"
                  value={buyProductsSearch}
                  onChange={(e) => setBuyProductsSearch(e.target.value)}
                  InputProps={{
                    sx: { height: 40 },
                    className: 'allow-interaction',
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Stack spacing={2}>
                {filteredBuyProducts.map((product) => (
                  <Card
                    key={`${product.id}-${product.originalIndex}`}
                    sx={{ border: 1, borderColor: 'rgba(59, 59, 59, 0.54)', boxShadow: 1 }}
                  >
                    <CardContent sx={{ px: 2, py: 1, '&:last-child': { pb: 1 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.80rem', color: '#003DA5' }}
                          >
                            {product.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.75rem', mb: 0.5, color: '#212b36' }}
                          >
                            Code: {product.code} | Stock: {getStockByType(product)}
                          </Typography>
                        </Box>
                        {!readOnly && (
                          <IconButton
                            onClick={() =>
                              handleRemoveProduct(`${product.id}-${product.originalIndex}`, 'buy')
                            }
                            size="small"
                            color="error"
                          >
                            <Iconify icon="solar:close-circle-outline" />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`buyProduct_${product.originalIndex}_type`]}>
                            <InputLabel className="commonColor">Product Type</InputLabel>
                            <Select
                              className="input-border"
                              value={getSelectedProductType(product)}
                              label="Product Type"
                              onChange={(e) =>
                                handleProductTypeChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'buy',
                                  e.target.value
                                )
                              }
                              MenuProps={buildMenuProps()}
                              disabled={readOnly}
                            >
                              {getProductTypeOptions(product.split).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`buyProduct_${product.originalIndex}_type`] && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`buyProduct_${product.originalIndex}_type`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 4 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`buyProduct_${product.originalIndex}_pricing`] || !!errors[`buyProduct_${product.originalIndex}_offerPrice`]}>
                            <InputLabel className="commonColor">Pricing</InputLabel>
                            <Select
                              className="input-border"
                              value={product.pricingType || 'sales'}
                              label="Pricing"
                              onChange={(e) =>
                                handleProductPricingChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'buy',
                                  e.target.value
                                )
                              }
                              disabled={readOnly}
                              renderValue={(selected) =>
                                selected === 'offer'
                                  ? `Offer`
                                  : `Standard`
                              }
                              MenuProps={buildMenuProps()}
                              sx={{
                                '& .MuiSelect-select': {
                                  whiteSpace: 'normal',
                                  display: 'block',
                                  lineHeight: 1.4,
                                },
                              }}
                            >
                              <MenuItem value="sales" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Standard
                              </MenuItem>
                              <MenuItem value="offer" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Offer
                              </MenuItem>
                            </Select>
                            {(errors[`buyProduct_${product.originalIndex}_pricing`] || errors[`buyProduct_${product.originalIndex}_offerPrice`]) && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`buyProduct_${product.originalIndex}_pricing`] || errors[`buyProduct_${product.originalIndex}_offerPrice`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        {/* <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={product.compulsory}
                                onChange={
                                  readOnly
                                    ? undefined
                                    : (e) =>
                                      handleProductCompulsoryChange(
                                        `${product.id}-${product.originalIndex}`,
                                        'buy',
                                        e.target.checked
                                      )
                                }
                                disabled={readOnly}
                                className="checkBox"

                              />
                            }
                            label="Compulsory"
                          />
                        </Grid> */}

                        {product.compulsory && (
                          <Grid item xs={12} sm={2}>
                            <TextField
                              fullWidth
                              className="input-border"
                              size="small"
                              label={
                                <span>
                                  Qty <span style={{ color: 'red' }}>*</span>
                                </span>
                              }
                              value={product.quantity}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === '' ||
                                  (/^\d+$/.test(value) && parseInt(value) <= 9999)
                                ) {
                                  handleProductQuantityChange(
                                    `${product.id}-${product.originalIndex}`,
                                    'buy',
                                    e.target.value
                                  );
                                }
                              }}
                              type="number"
                              inputProps={{ min: 1, max: 9999 }}
                              InputProps={{ readOnly: readOnly }}
                              onWheel={(e) => e.target.blur()}
                              sx={{
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                              }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={4}>
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!errors[`buyProduct_${product.originalIndex}_group`]}
                            disabled={readOnly}
                          >
                            <InputLabel id={`buy-group-label-${product.originalIndex}`} className="commonColor" shrink>
                              Group
                            </InputLabel>
                            <Select
                              labelId={`buy-group-label-${product.originalIndex}`}
                              className="input-border"
                              label="Group"
                              
                              value={
                                PROMOTION_GROUP_OPTIONS.some((o) => o.value === String(product.groupNo))
                                  ? String(product.groupNo)
                                  : ''
                              }
                              displayEmpty
                              onChange={(e) =>
                                updateProductGroupNo(product.originalIndex, e.target.value)
                              }
                              MenuProps={buildMenuProps()}
                              renderValue={(selected) =>
                                selected
                                  ? PROMOTION_GROUP_OPTIONS.find((o) => o.value === selected)?.label ||
                                  `Group ${selected}`
                                  : 'Select group'
                              }
                              sx={{
                                '& .MuiSelect-select': {
                                  whiteSpace: 'normal',
                                  display: 'block',
                                  lineHeight: 1.4,
                                },
                              }}
                            >
                              <MenuItem value="" disabled sx={{ fontSize: '0.875rem' }}>
                                Select group
                              </MenuItem>
                              {PROMOTION_GROUP_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.875rem' }}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`buyProduct_${product.originalIndex}_group`] && (
                              <FormHelperText>
                                {errors[`buyProduct_${product.originalIndex}_group`]}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>

                      {formData.buyDiscountApplyTo === 'per-item' && (
                        <Box
                          sx={{
                            mt: 2,
                            bgcolor: '#fffbea',
                            border: 1,
                            borderColor: '#ffd700',
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Item Discount (Optional)
                            </Typography>
                            <Checkbox
                              checked={!!(product.buyItemDiscountValue && product.buyItemDiscountValue !== '' && parseFloat(product.buyItemDiscountValue) > 0)}
                              disabled={readOnly}
                              size="small"
                              className='checkBox'
             
                            />
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Discount Type</InputLabel>
                                <Select
                                className='input-border text-field'
                                  value={product.buyItemDiscountType || 'percentage'}
                                  label="Discount Type"
                                  onChange={(e) => {
                                    const newBuyProducts = [...formData.buyProducts];
                                    newBuyProducts[product.originalIndex] = {
                                      ...newBuyProducts[product.originalIndex],
                                      buyItemDiscountType: e.target.value,
                                    };
                                    handleInputChange('buyProducts', newBuyProducts);
                                  }}
                                  MenuProps={buildMenuProps()}
                                  disabled={readOnly}
                          
                                >
                                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                                  <MenuItem value="fixed">Fixed Amount (£)</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                className='input-border text-field'
                                size="small"
                                label="Discount Value"
                                placeholder="e.g., 10"
                                value={product.buyItemDiscountValue || ''}
                                onChange={(e) => {
                                  let raw = e.target.value;
                                  const discountType = product.buyItemDiscountType || 'percentage';
                                  if (discountType === 'percentage') {
                                    if (raw === '' || raw === '.') {
                                      const newBuyProducts = [...formData.buyProducts];
                                      newBuyProducts[product.originalIndex] = {
                                        ...newBuyProducts[product.originalIndex],
                                        buyItemDiscountValue: raw,
                                      };
                                      handleInputChange('buyProducts', newBuyProducts);
                                      return;
                                    }
                                    const num = parseFloat(raw);
                                    if (!Number.isNaN(num)) {
                                      if (num < 0) {
                                        const newBuyProducts = [...formData.buyProducts];
                                        newBuyProducts[product.originalIndex] = {
                                          ...newBuyProducts[product.originalIndex],
                                          buyItemDiscountValue: '0',
                                        };
                                        handleInputChange('buyProducts', newBuyProducts);
                                        return;
                                      }
                                      if (num > 100) {
                                        const newBuyProducts = [...formData.buyProducts];
                                        newBuyProducts[product.originalIndex] = {
                                          ...newBuyProducts[product.originalIndex],
                                          buyItemDiscountValue: '100',
                                        };
                                        handleInputChange('buyProducts', newBuyProducts);
                                        return;
                                      }
                                    }
                                  }
                                  if (discountType === 'fixed' && !Number.isNaN(parseFloat(raw))) {
                                    const num = parseFloat(raw);
                                    if (num < 0) {
                                      const newBuyProducts = [...formData.buyProducts];
                                      newBuyProducts[product.originalIndex] = {
                                        ...newBuyProducts[product.originalIndex],
                                        buyItemDiscountValue: '0',
                                      };
                                      handleInputChange('buyProducts', newBuyProducts);
                                      return;
                                    }
                                    const productPrice = getPriceByType(product, product.pricingType || 'sales');
                                    if (productPrice > 0 && num >= productPrice) return;
                                  }
                                  const sanitized = limitDecimals(raw, 2);
                                  if (discountType === 'percentage') {
                                    const num = parseFloat(sanitized);
                                    if (!Number.isNaN(num)) {
                                      if (num < 0) {
                                        const newBuyProducts = [...formData.buyProducts];
                                        newBuyProducts[product.originalIndex] = {
                                          ...newBuyProducts[product.originalIndex],
                                          buyItemDiscountValue: '0',
                                        };
                                        handleInputChange('buyProducts', newBuyProducts);
                                        return;
                                      }
                                      if (num > 100) {
                                        const newBuyProducts = [...formData.buyProducts];
                                        newBuyProducts[product.originalIndex] = {
                                          ...newBuyProducts[product.originalIndex],
                                          buyItemDiscountValue: '100',
                                        };
                                        handleInputChange('buyProducts', newBuyProducts);
                                        return;
                                      }
                                    }
                                  }
                                  const newBuyProducts = [...formData.buyProducts];
                                  newBuyProducts[product.originalIndex] = {
                                    ...newBuyProducts[product.originalIndex],
                                    buyItemDiscountValue: sanitized,
                                  };
                                  handleInputChange('buyProducts', newBuyProducts);
                                }}
                                onKeyDown={(e) => {
                                  if (['e', 'E', '+', '-'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onWheel={(e) => {
                                  e.target.blur();
                                  e.preventDefault();
                                }}
                                type="number"
                                inputProps={{
                                  min: 0,
                                  max: (product.buyItemDiscountType || 'percentage') === 'percentage'
                                    ? 100
                                    : (() => {
                                      const productPrice = getPriceByType(product, product.pricingType || 'sales');
                                      return productPrice > 0 ? productPrice - 0.01 : 999999;
                                    })(),
                                  step: (product.buyItemDiscountType || 'percentage') === 'percentage' ? 1 : 0.01,
                                }}
                                onBlur={(e) => {
                                  const num = parseFloat(e.target.value);
                                  if (!Number.isNaN(num)) {
                                    const discountType = product.buyItemDiscountType || 'percentage';
                                    if (discountType === 'percentage') {
                                      if (num < 0) {
                                        const newBuyProducts = [...formData.buyProducts];
                                        newBuyProducts[product.originalIndex] = {
                                          ...newBuyProducts[product.originalIndex],
                                          buyItemDiscountValue: '0',
                                        };
                                        handleInputChange('buyProducts', newBuyProducts);
                                      } else if (num > 100) {
                                        const newBuyProducts = [...formData.buyProducts];
                                        newBuyProducts[product.originalIndex] = {
                                          ...newBuyProducts[product.originalIndex],
                                          buyItemDiscountValue: '100',
                                        };
                                        handleInputChange('buyProducts', newBuyProducts);
                                      }
                                    } else if (discountType === 'fixed') {
                                      if (num < 0) {
                                        const newBuyProducts = [...formData.buyProducts];
                                        newBuyProducts[product.originalIndex] = {
                                          ...newBuyProducts[product.originalIndex],
                                          buyItemDiscountValue: '0',
                                        };
                                        handleInputChange('buyProducts', newBuyProducts);
                                      } else {
                                        const productPrice = getPriceByType(product, product.pricingType || 'sales');
                                        if (productPrice > 0 && num >= productPrice) {
                                          const newBuyProducts = [...formData.buyProducts];
                                          newBuyProducts[product.originalIndex] = {
                                            ...newBuyProducts[product.originalIndex],
                                            buyItemDiscountValue: (productPrice - 0.01).toFixed(2),
                                          };
                                          handleInputChange('buyProducts', newBuyProducts);
                                        }
                                      }
                                    }
                                  }
                                }}
                                InputProps={{ readOnly: readOnly }}
                                error={
                                  !!errors[`buyProduct_${product.originalIndex}_discountValue`]
                                }
                                helperText={
                                  errors[`buyProduct_${product.originalIndex}_discountValue`] ||
                                  ((product.buyItemDiscountType || 'percentage') === 'percentage' ? 'Maximum 100%' : '')
                                }
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, fontSize: '1rem' }}>
            Get Products (Discounted)
          </Typography>

          <TextField
            fullWidth
            className="input-border text-field-promo"
            label={
              <span>
                Total Get Quantity<span style={{ color: 'red' }}>*</span>
              </span>
            }
            value={formData.getQuantity}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                handleInputChange('getQuantity', value);
              }
            }}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onWheel={(e) => e.target.blur()}
            placeholder="Enter total get quantity"
            type="number"
            inputProps={{ min: 1, max: 9999 }}
            InputProps={{
              readOnly: readOnly,
              sx: { height: 40, width: '50%' }
            }}
            error={!!errors.getQuantity}
            helperText={errors.getQuantity}
            sx={{
              mb: 1,
           
            }}
          />

          {renderGroupQtyPanel({
            usedGroups: usedGetGroups,
            quantities: formData.getGroupQuantities || {},
            onChange: updateGetGroupQty,
            totalQty: formData.getQuantity,
            errorKey: 'getGroupQty',
            sideLabel: 'Get',
          })}

          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Discount Configuration
            </Typography>
            <RadioGroup
              value={formData.getDiscountType || 'set-value'}
              onChange={(e) => handleInputChange('getDiscountType', e.target.value)}
              row
            >
              <FormControlLabel value="set-value" control={<Radio className="checkBox" disabled={readOnly} />} label="Set Value" />
              <FormControlLabel value="value-off" control={<Radio className="checkBox" disabled={readOnly} />} label="Value Off" />
            </RadioGroup>

            {(!formData.getDiscountType || formData.getDiscountType === 'set-value') && (
              <>
                <TextField
                  fullWidth
                  className="input-border text-field"
                  size="small"
                  label="Set Value Amount (£)"
                  placeholder="e.g., 11.99"
                  value={formData.getSetValue || ''}
                  onChange={(e) => {
                    const sanitized = limitDecimals(e.target.value, 2);
                    handleInputChange('getSetValue', sanitized);
                  }}
                  onKeyDown={(e) => {
                    if (['e', 'E', '+', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.target.blur()}
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  InputProps={{
                    readOnly: readOnly,
                    sx: { height: 40, }
                  }}
                  error={!!errors.getSetValue || !!errors.getSetValueWarning}
                  helperText={errors.getSetValue || errors.getSetValueWarning}
                  sx={{
                    mt: 1,
                  }}
                />
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#212b36' }}>
                  Total price for all get products combined
                </Typography>
              </>
            )}

            {formData.getDiscountType === 'value-off' && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                Configure discount to reduce the price of get products
              </Typography>
            )}
          </Box>

          {formData.getDiscountType === 'value-off' && (
            <Box
              sx={{
                bgcolor: '#fffbea',
                border: 1,
                borderColor: '#ffd700',
                p: 2,
                mb: 1,
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Item Discount on Get Products (Optional)
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Apply Discount To</InputLabel>
                <Select
                  
                  value={formData.getItemDiscountApplyTo || 'bulk'}
                  label="Apply Discount To"
                  onChange={(e) => handleInputChange('getItemDiscountApplyTo', e.target.value)}
                  MenuProps={buildMenuProps({
                    disablePortal: true,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                  })}
                  disabled={readOnly}
                >
                  <MenuItem value="bulk">Bulk (All Get Products Total)</MenuItem>
                  <MenuItem value="per-item">Per Item (Each get products)</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#d32f2f' }}>
                Per Item: Configure discount for each get product individually
                <br />
                Bulk: Apply single discount to total of all get products
              </Typography>

              {(formData.getItemDiscountApplyTo === 'bulk' || !formData.getItemDiscountApplyTo) && (
                <>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      className='input-border'
                      value={formData.getItemDiscountType || 'percentage'}
                      label="Discount Type"
                      onChange={(e) => handleInputChange('getItemDiscountType', e.target.value)}
                      MenuProps={buildMenuProps({
                        disablePortal: true,
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      })}
                      disabled={readOnly}
                    >
                      <MenuItem value="percentage">Percentage %</MenuItem>
                      <MenuItem value="fixed">Fixed Amount</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <TextField
                      className='input-border text-field'
                      fullWidth
                      size="small"
                      label="Discount Value"
                      placeholder="Enter discount value"
                      value={formData.getItemDiscountValue || ''}
                      onChange={(e) => {
                        let raw = e.target.value;
                        if (formData.getItemDiscountType === 'percentage') {
                          const num = parseFloat(raw);
                          if (!Number.isNaN(num) && num > 100) {
                            handleInputChange('getItemDiscountValue', '100');
                            return;
                          }
                          if (raw === '' || raw === '.') {
                            handleInputChange('getItemDiscountValue', raw);
                            return;
                          }
                        }
                        const sanitized = limitDecimals(raw, 2);
                        if (formData.getItemDiscountType === 'percentage') {
                          const num = parseFloat(sanitized);
                          if (!Number.isNaN(num) && num > 100) {
                            handleInputChange('getItemDiscountValue', '100');
                            return;
                          }
                        }
                        handleInputChange('getItemDiscountValue', sanitized);
                      }}
                      onBlur={(e) => {
                        if (formData.getItemDiscountType === 'percentage') {
                          const num = parseFloat(e.target.value);
                          if (!Number.isNaN(num) && num > 100) {
                            handleInputChange('getItemDiscountValue', '100');
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      type="number"
                      inputProps={{
                        min: 0,
                        max: formData.getItemDiscountType === 'percentage' ? 100 : 999999,
                        step: formData.getItemDiscountType === 'percentage' ? 1 : 0.01,
                      }}
                      InputProps={{ readOnly: readOnly }}
                      error={!!errors.getItemDiscountValue && !errors.getItemDiscountValue?.startsWith('Warning:')}
                    />
                    {errors.getItemDiscountValue?.startsWith('Warning:') ? (
                      <FormHelperText sx={{ color: '#ed6c02', fontSize: '0.75rem', mt: 0.5, mx: 1.75 }}>
                        {errors.getItemDiscountValue}
                      </FormHelperText>
                    ) : errors.getItemDiscountValue ? (
                      <FormHelperText error sx={{ mt: 0.5, mx: 1.75 }}>
                        {errors.getItemDiscountValue}
                      </FormHelperText>
                    ) : formData.getItemDiscountType === 'percentage' ? (
                      <FormHelperText sx={{ mt: 0.5, mx: 1.75, color: '#000' }}>
                        Maximum 100%
                      </FormHelperText>
                    ) : null}
                  </FormControl>
                </>
              )}
            </Box>
          )}

          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Selected Products ({formData.getProducts.length})
              </Typography>
              {errors.getProducts && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {errors.getProducts}
                </Typography>
              )}
            </Box>
            {!readOnly && (
              <Button
                variant="outlined"
                className="button-variant-outlined"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() => handleOpenProductModal('get')}
                disabled={formData.getProducts.length >= 200}
                disableRipple
              >
                Add More Products
              </Button>
            )}
          </Box>
          {formData.getProducts.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 400, overflowY: 'auto', pr: 1 }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  pb: 1,
                  mb: 1,

                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <TextField
                  className="allow-interaction input-border"
                  fullWidth
                  placeholder="Search by product name or product code"
                  value={getProductsSearch}
                  onChange={(e) => setGetProductsSearch(e.target.value)}
                  InputProps={{
                    className: 'allow-interaction',
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" />
                      </InputAdornment>
                    ),
                    sx: { height: 40 },
                  }}
                />
              </Box>
              <Stack spacing={2}>
                {filteredGetProducts.map((product) => (
                  <Card
                    key={`${product.id}-${product.originalIndex}`}
                    sx={{ border: 1, borderColor: 'rgba(59, 59, 59, 0.54)', boxShadow: 1 }}
                  >
                    <CardContent sx={{ px: 2, py: 1, '&:last-child': { pb: 1 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.80rem', color: '#003DA5' }}
                          >
                            {product.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.75rem', mb: 0.5, color: '#212b36' }}
                          >
                            Code: {product.code} | Stock: {getStockByType(product)}
                          </Typography>
                        </Box>
                        {!readOnly && (
                          <IconButton
                            onClick={() =>
                              handleRemoveProduct(`${product.id}-${product.originalIndex}`, 'get')
                            }
                            size="small"
                            color="error"
                          >
                            <Iconify icon="solar:close-circle-outline" />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`getProduct_${product.originalIndex}_type`]}>
                            <InputLabel className="commonColor">Product Type</InputLabel>
                            <Select
                              className="input-border"
                              value={getSelectedProductType(product)}
                              label="Product Type"
                              onChange={(e) =>
                                handleProductTypeChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'get',
                                  e.target.value
                                )
                              }
                              MenuProps={buildMenuProps()}
                              disabled={readOnly}
                            >
                              {getProductTypeOptions(product.split).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`getProduct_${product.originalIndex}_type`] && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`getProduct_${product.originalIndex}_type`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={product.compulsory ? 4 : 4}>
                          <FormControl fullWidth size="small" error={!!errors[`getProduct_${product.originalIndex}_pricing`] || !!errors[`getProduct_${product.originalIndex}_offerPrice`]}>
                            <InputLabel className="commonColor">Pricing</InputLabel>
                            <Select
                              className="input-border"
                              value={product.pricingType || 'sales'}
                              label="Pricing"
                              onChange={(e) =>
                                handleProductPricingChange(
                                  `${product.id}-${product.originalIndex}`,
                                  'get',
                                  e.target.value
                                )
                              }
                              disabled={readOnly}
                              renderValue={(selected) =>
                                selected === 'offer'
                                  ? `Offer`
                                  : `Standard`
                              }
                              MenuProps={buildMenuProps()}
                              sx={{
                                '& .MuiSelect-select': {
                                  whiteSpace: 'normal',
                                  display: 'block',
                                  lineHeight: 1.4,
                                },
                              }}
                            >
                              <MenuItem value="sales" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Standard
                              </MenuItem>
                              <MenuItem value="offer" sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                Offer
                              </MenuItem>
                            </Select>
                            {(errors[`getProduct_${product.originalIndex}_pricing`] || errors[`getProduct_${product.originalIndex}_offerPrice`]) && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {errors[`getProduct_${product.originalIndex}_pricing`] || errors[`getProduct_${product.originalIndex}_offerPrice`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        {/* <Grid item xs={12} sm={product.compulsory ? 3 : 4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={product.compulsory}
                                onChange={
                                  readOnly
                                    ? undefined
                                    : (e) =>
                                      handleProductCompulsoryChange(
                                        `${product.id}-${product.originalIndex}`,
                                        'get',
                                        e.target.checked
                                      )
                                }
                                disabled={readOnly}
                                className="checkBox"
                              />
                            }
                            label="Compulsory"
                          />
                        </Grid> */}

                        {product.compulsory && (
                          <Grid item xs={12} sm={2}>
                            <TextField
                              fullWidth
                              className="input-border"
                              size="small"
                              label={
                                <span>
                                  Qty <span style={{ color: 'red' }}>*</span>
                                </span>
                              }
                              value={product.quantity}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === '' ||
                                  (/^\d+$/.test(value) && parseInt(value) <= 9999)
                                ) {
                                  handleProductQuantityChange(
                                    `${product.id}-${product.originalIndex}`,
                                    'get',
                                    e.target.value
                                  );
                                }
                              }}
                              type="number"
                              inputProps={{ min: 1, max: 9999 }}
                              InputProps={{ readOnly: readOnly }}
                              onWheel={(e) => e.target.blur()}
                              sx={{
                                '& input[type=number]': { MozAppearance: 'textfield' },
                                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                              }}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} sm={4}>
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!errors[`getProduct_${product.originalIndex}_group`]}
                            disabled={readOnly}
                          >
                            <InputLabel id={`get-group-label-${product.originalIndex}`} className="commonColor" shrink>
                              Group
                            </InputLabel>
                            <Select
                              labelId={`get-group-label-${product.originalIndex}`}
                              className="input-border"
                              label="Group"
                              value={
                                PROMOTION_GROUP_OPTIONS.some((o) => o.value === String(product.groupNo))
                                  ? String(product.groupNo)
                                  : ''
                              }
                              displayEmpty
                              onChange={(e) =>
                                updateGetProductGroupNo(product.originalIndex, e.target.value)
                              }
                              MenuProps={buildMenuProps()}
                              renderValue={(selected) =>
                                selected
                                  ? PROMOTION_GROUP_OPTIONS.find((o) => o.value === selected)?.label ||
                                  `Group ${selected}`
                                  : 'Select group'
                              }
                              sx={{
                                '& .MuiSelect-select': {
                                  whiteSpace: 'normal',
                                  display: 'block',
                                  lineHeight: 1.4,
                                },
                              }}
                            >
                              <MenuItem value="" disabled sx={{ fontSize: '0.875rem' }}>
                                Select group
                              </MenuItem>
                              {PROMOTION_GROUP_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.875rem' }}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`getProduct_${product.originalIndex}_group`] && (
                              <FormHelperText>
                                {errors[`getProduct_${product.originalIndex}_group`]}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>

                      {formData.getItemDiscountApplyTo === 'per-item' && (
                        <Box
                          sx={{
                            mt: 2,
                            bgcolor: '#fffbea',
                            border: 1,
                            borderColor: '#ffd700',
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Item Discount (Optional)
                            </Typography>
                            <Checkbox
                              checked={!!(product.itemDiscountValue && product.itemDiscountValue !== '' && parseFloat(product.itemDiscountValue) > 0)}
                              disabled={readOnly}
                              size="small"
                              className='checkBox'
                            />
                          </Box>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Discount Type</InputLabel>
                                <Select
                                  className='input-border '
                                  value={product.itemDiscountType || 'percentage'}
                                  label="Discount Type"
                                  onChange={(e) =>
                                    handleProductItemDiscountTypeChange(
                                      `${product.id}-${product.originalIndex}`,
                                      e.target.value
                                    )
                                  }
                                  MenuProps={buildMenuProps()}
                                  disabled={readOnly}
                                >
                                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                className='input-border text-field'
                                size="small"
                                label="Discount Value"
                                placeholder="e.g., 10"
                                value={product.itemDiscountValue || ''}
                                onChange={(e) => {
                                  let raw = e.target.value;
                                  const discountType = product.itemDiscountType || 'percentage';
                                  if (discountType === 'percentage') {
                                    if (raw === '' || raw === '.') {
                                      handleProductItemDiscountValueChange(
                                        `${product.id}-${product.originalIndex}`,
                                        raw
                                      );
                                      return;
                                    }
                                    const num = parseFloat(raw);
                                    if (!Number.isNaN(num)) {
                                      if (num < 0) {
                                        handleProductItemDiscountValueChange(
                                          `${product.id}-${product.originalIndex}`,
                                          '0'
                                        );
                                        return;
                                      }
                                      if (num > 100) {
                                        handleProductItemDiscountValueChange(
                                          `${product.id}-${product.originalIndex}`,
                                          '100'
                                        );
                                        return;
                                      }
                                    }
                                  }
                                  if (discountType === 'fixed' && !Number.isNaN(parseFloat(raw))) {
                                    const num = parseFloat(raw);
                                    if (num < 0) {
                                      handleProductItemDiscountValueChange(
                                        `${product.id}-${product.originalIndex}`,
                                        '0'
                                      );
                                      return;
                                    }
                                    const productPrice = getPriceByType(product, product.pricingType || 'sales');
                                    if (productPrice > 0 && num >= productPrice) {
                                      return;
                                    }
                                  }
                                  const sanitized = limitDecimals(raw, 2);
                                  if (discountType === 'percentage') {
                                    const num = parseFloat(sanitized);
                                    if (!Number.isNaN(num)) {
                                      if (num < 0) {
                                        handleProductItemDiscountValueChange(
                                          `${product.id}-${product.originalIndex}`,
                                          '0'
                                        );
                                        return;
                                      }
                                      if (num > 100) {
                                        handleProductItemDiscountValueChange(
                                          `${product.id}-${product.originalIndex}`,
                                          '100'
                                        );
                                        return;
                                      }
                                    }
                                  }
                                  handleProductItemDiscountValueChange(
                                    `${product.id}-${product.originalIndex}`,
                                    sanitized
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (['e', 'E', '+', '-'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onWheel={(e) => {
                                  e.target.blur();
                                  e.preventDefault();
                                }}
                                type="number"
                                inputProps={{
                                  min: 0,
                                  max: (product.itemDiscountType || 'percentage') === 'percentage'
                                    ? 100
                                    : (() => {
                                      const productPrice = getPriceByType(product, product.pricingType || 'sales');
                                      return productPrice > 0 ? productPrice - 0.01 : 999999;
                                    })(),
                                  step: (product.itemDiscountType || 'percentage') === 'percentage' ? 1 : 0.01,
                                }}
                                InputProps={{ readOnly: readOnly }}
                                onBlur={(e) => {
                                  const num = parseFloat(e.target.value);
                                  if (!Number.isNaN(num)) {
                                    const discountType = product.itemDiscountType || 'percentage';
                                    if (discountType === 'percentage') {
                                      if (num < 0) {
                                        handleProductItemDiscountValueChange(
                                          `${product.id}-${product.originalIndex}`,
                                          '0'
                                        );
                                      } else if (num > 100) {
                                        handleProductItemDiscountValueChange(
                                          `${product.id}-${product.originalIndex}`,
                                          '100'
                                        );
                                      }
                                    } else if (discountType === 'fixed') {
                                      if (num < 0) {
                                        handleProductItemDiscountValueChange(
                                          `${product.id}-${product.originalIndex}`,
                                          '0'
                                        );
                                      } else {
                                        const productPrice = getPriceByType(product, product.pricingType || 'sales');
                                        if (productPrice > 0 && num >= productPrice) {
                                          handleProductItemDiscountValueChange(
                                            `${product.id}-${product.originalIndex}`,
                                            (productPrice - 0.01).toFixed(2)
                                          );
                                        }
                                      }
                                    }
                                  }
                                }}
                                error={
                                  !!errors[`getProduct_${product.originalIndex}_discountValue`]
                                }
                                helperText={
                                  errors[`getProduct_${product.originalIndex}_discountValue`] ||
                                  ((product.itemDiscountType || 'percentage') === 'percentage' ? 'Maximum 100%' : '')
                                }
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
